import React, { useRef, useEffect } from 'react';
import { confirmAlert } from 'components/common';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FaArrowLeft, FaPlus, FaList, FaEllipsisV, FaUser } from 'react-icons/fa';
import { MdEdit, MdDoneAll, MdOutlinePending, MdDeleteForever } from 'react-icons/md';
import { RiMailSendFill } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import EditRequestUserAccountForm from './EditRequestUserAccountForm';
import CreateUserEmployeeForm from './CreateUserEmployeeForm';
import CloseRequestUserAccountConfirm from './CloseRequestUserAccountConfirm';
import HoldToProcessForm from './HoldToProcessForm';
import { reSendEmailUserAccount, deleteRequestUserAccount } from 'redux/request/userAccountSlice';

const RequestUserAccountDetailHeader = ({ record }) => {
  const [showRequestForm, setShowRequestForm] = React.useState(false);
  const [showCreateForm, setShowCreateForm] = React.useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = React.useState(false);
  const [showHoldToProcess, setShowHoldToProcess] = React.useState(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    allow_create_user,
    allow_edit,
    allow_delete,
    allow_close,
    allow_resent_email,
    allow_hold_to_process,
  } = record?.permissions || {};

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRequestList = () => {
    navigate('/request/user-accounts/list');
  };

  const handleNewRequest = () => {
    navigate('/request/user-accounts/new');
  };

  const handleEditRequest = () => {
    setDropdownOpen(false);
    setShowRequestForm(true);
  };

  const handleReSendEmail = () => {
    setDropdownOpen(false);
    confirmAlert({
      title: 'Confirmation',
      message: 'Are you sure you want to resend the email?',
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          className: 'confirm-btn-primary',
          onClick: async () => {
            try {
              await dispatch(reSendEmailUserAccount(record.id)).unwrap();
              toast.success('Email has been resent successfully');
            } catch (error) {
              toast.error(error?.message || 'Failed to resend email');
            }
          },
        },
        {
          label: 'No',
          onClick: () => {},
        },
      ],
    });
  };

  const handleDeleteRequest = () => {
    setDropdownOpen(false);
    confirmAlert({
      title: 'Confirmation',
      message: 'Are you sure you want to delete this request?',
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          className: 'confirm-btn-danger',
          onClick: async () => {
            try {
              await dispatch(deleteRequestUserAccount(record.id)).unwrap();
              toast.success(`Request ${record.id} deleted successfully.`);
              handleRequestList();
            } catch (error) {
              toast.error(error?.message || 'Failed to delete request');
            }
          },
        },
        {
          label: 'No',
          onClick: () => {},
        },
      ],
    });
  };

  const hasAnyAction =
    allow_hold_to_process ||
    allow_create_user ||
    allow_resent_email ||
    allow_close ||
    allow_edit ||
    allow_delete;

  return (
    <>
      <div className="tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-slate-100 tw-mb-3">
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-md tw-bg-slate-100 tw-text-slate-700 hover:tw-bg-slate-200 tw-border-0 tw-cursor-pointer tw-transition-colors"
          >
            <FaArrowLeft size={12} />
          </button>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={handleNewRequest}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-md tw-bg-slate-100 tw-text-slate-700 hover:tw-bg-slate-200 tw-border-0 tw-cursor-pointer tw-transition-colors"
            >
              <FaPlus size={10} />
              <span className="tw-hidden xl:tw-inline-block">New Request</span>
            </button>

            <button
              type="button"
              onClick={handleRequestList}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-md tw-bg-slate-100 tw-text-slate-700 hover:tw-bg-slate-200 tw-border-0 tw-cursor-pointer tw-transition-colors"
            >
              <FaList size={10} />
              <span className="tw-hidden xl:tw-inline-block">Back to list</span>
            </button>

            {hasAnyAction && (
              <div className="tw-relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-md tw-bg-slate-100 tw-text-slate-700 hover:tw-bg-slate-200 tw-border-0 tw-cursor-pointer tw-transition-colors"
                >
                  <FaEllipsisV size={12} />
                </button>

                {dropdownOpen && (
                  <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-z-50 tw-min-w-[180px] tw-bg-white tw-border tw-border-slate-200 tw-rounded tw-shadow-lg tw-py-1">
                    {allow_hold_to_process && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setDropdownOpen(false); setShowHoldToProcess(true); }}
                          className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-yellow-600 hover:tw-bg-slate-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-text-left"
                        >
                          <MdOutlinePending size={14} />
                          On hold
                        </button>
                        <div className="tw-border-t tw-border-slate-100 tw-my-0.5" />
                      </>
                    )}

                    {allow_create_user && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setDropdownOpen(false); setShowCreateForm(true); }}
                          className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-blue-600 hover:tw-bg-slate-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-text-left"
                        >
                          <FaUser size={12} />
                          Create User Account
                        </button>
                        <div className="tw-border-t tw-border-slate-100 tw-my-0.5" />
                      </>
                    )}

                    {allow_resent_email && (
                      <>
                        <button
                          type="button"
                          onClick={handleReSendEmail}
                          className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-yellow-600 hover:tw-bg-slate-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-text-left"
                        >
                          <RiMailSendFill size={14} />
                          Resend email
                        </button>
                        <div className="tw-border-t tw-border-slate-100 tw-my-0.5" />
                      </>
                    )}

                    {allow_close && (
                      <>
                        <button
                          type="button"
                          onClick={() => { setDropdownOpen(false); setShowCloseConfirm(true); }}
                          className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-green-600 hover:tw-bg-slate-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-text-left"
                        >
                          <MdDoneAll size={14} />
                          Close (No action needed)
                        </button>
                        <div className="tw-border-t tw-border-slate-100 tw-my-0.5" />
                      </>
                    )}

                    {allow_edit && (
                      <>
                        <button
                          type="button"
                          onClick={handleEditRequest}
                          className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-text-left"
                        >
                          <MdEdit size={14} className="tw-text-blue-500" />
                          Edit
                        </button>
                        <div className="tw-border-t tw-border-slate-100 tw-my-0.5" />
                      </>
                    )}

                    {allow_delete && (
                      <button
                        type="button"
                        onClick={handleDeleteRequest}
                        className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-text-left"
                      >
                        <MdDeleteForever size={14} className="tw-text-red-500" />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showRequestForm && (
        <EditRequestUserAccountForm
          open={showRequestForm}
          setOpen={setShowRequestForm}
          record={record}
        />
      )}

      {showCreateForm && (
        <CreateUserEmployeeForm
          open={showCreateForm}
          setOpen={setShowCreateForm}
          record={record}
        />
      )}

      {showCloseConfirm && (
        <CloseRequestUserAccountConfirm
          open={showCloseConfirm}
          setOpen={setShowCloseConfirm}
          record={record}
        />
      )}

      {showHoldToProcess && (
        <HoldToProcessForm
          open={showHoldToProcess}
          setOpen={setShowHoldToProcess}
          record={record}
        />
      )}
    </>
  );
};

RequestUserAccountDetailHeader.propTypes = {
  record: PropTypes.object.isRequired,
};

export default RequestUserAccountDetailHeader;
